import React from 'react';

import { throttle } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
    LIST_MONITORING_HEALTH_COMPONENTS_RESOURCE,
    useListMonitoringHealthComponents,
} from 'src/features/monitoring/api/useListMonitoringHealthComponents';
import MonitoringDevicesSection from 'src/features/monitoring/components/MonitoringDevicesSection';
import MonitoringTabRow from 'src/features/monitoring/components/MonitoringTabRow';
import { MonitoringHealthComponent } from 'src/features/monitoring/types';
import { SYSTEM_COMPONENT, SYSTEM_COMPONENT_TYPE } from 'src/features/systems/utils/types';
import { systemsMessages } from 'src/features/systems/utils/messages';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { HealthSubjectKind, WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { ICON_MAP } from 'src/utils/constants';

const CORE_PROCESS_COMPONENTS = new Set<SYSTEM_COMPONENT>([
    SYSTEM_COMPONENT.BACKEND_HTTP_SERVICE,
    SYSTEM_COMPONENT.BACKEND_WORKER_SERVICE,
    SYSTEM_COMPONENT.BACKEND_INFRA_SERVICE,
]);

export default function MonitoringDevices() {
    const { parkingId } = useParking();
    const { data, isLoading, isError } = useListMonitoringHealthComponents({ parkingId, limit: 100 });
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    const handleWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.HEALTH_STATUS_UPDATED &&
                    event.data.subject.kind === HealthSubjectKind.COMPONENT
                ) {
                    void queryClient.invalidateQueries({
                        queryKey: [LIST_MONITORING_HEALTH_COMPONENTS_RESOURCE],
                    });
                }
            }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.MONITORING_HEALTH, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.MONITORING_HEALTH, handleWebSocket);
    }, [handleWebSocket, subscribe, unsubscribe]);

    const { hardware, software, coreProcesses } = React.useMemo(() => {
        const hw: MonitoringHealthComponent[] = [];
        const sw: MonitoringHealthComponent[] = [];
        const cp: MonitoringHealthComponent[] = [];
        data?.results.forEach((device) => {
            if (CORE_PROCESS_COMPONENTS.has(device.component)) {
                cp.push(device);
            } else if (device.component_type === SYSTEM_COMPONENT_TYPE.HARDWARE_DEVICE) {
                hw.push(device);
            } else {
                sw.push(device);
            }
        });
        const byName = (a: MonitoringHealthComponent, b: MonitoringHealthComponent) =>
            a.name.localeCompare(b.name);
        hw.sort(byName);
        sw.sort(byName);
        cp.sort(byName);
        return { hardware: hw, software: sw, coreProcesses: cp };
    }, [data]);

    return (
        <>
            <MonitoringTabRow />

            {isError && (
                <GeneralMessageBlock
                    className="py-12"
                    variant="error"
                    icon={ICON_MAP.CIRCLE_EXCLAMATION}
                />
            )}

            <MonitoringDevicesSection
                title={<FormattedMessage {...systemsMessages.hardwareDevices} />}
                devices={hardware}
                isLoading={isLoading}
            />
            <MonitoringDevicesSection
                title={<FormattedMessage {...systemsMessages.softwareServices} />}
                devices={software}
                isLoading={isLoading}
            />
            <MonitoringDevicesSection
                title={<FormattedMessage {...systemsMessages.coreProcesses} />}
                devices={coreProcesses}
                isLoading={isLoading}
            />
        </>
    );
}
